import { _lang, isSystem, lang } from '@umu/ukit-lang';
import { contactSales } from '@umu/umu-api';
import type { IOutputContactSales } from '@umu/umu-api/src/services/contractSales/contractSales';
import { Checkbox, Select } from '@umu/umu-design';
import type { CheckboxChangeEvent } from '@umu/umu-design/es/components/Checkbox';
import { $document, $window } from '@umu/umu-dom';
import { localStorageHelper, STORAGE_KEY } from '@umu/umu-storage';
import { useDebounceFn } from 'ahooks';
import type { IChangeCallbackArgs } from 'businessCommon';
import { MailInput, PhoneInput } from 'businessCommon/src/components';
import { adapterService, validateMail } from 'businessCommon/src/services';
import { Button, CustomDialog, If, Textarea } from 'common/src/components';
import { handlePromiseError } from 'common/utils/errorHandler';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { isCn, isCom, isCoSite, isIo } from 'utils/util';
import { amountOptionList, getDefaultState, jobOptionList } from './dataFlow';
import './index.scss';
import { ChannelType, FormType, type IProp, type OptionItemType, type ParamsType } from './interface';

const CN_NAME_REGEX = /^([\u4e00-\u9fa5]+|[a-zA-Z]+(\s[a-zA-Z]+)*)$/;
const CN_HAS_LETTER_REGEX = /[\u4e00-\u9fa5a-zA-Z]/;

const scrollIntoView = () => {
    const errorTipNode = document.querySelector('.form-tip.error');
    if (errorTipNode) {
        errorTipNode.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

function goThankYou() {
    $window.location.href = '/cms/thank-you?from=contact-sales';
}

function Form(props: IProp) {
    const [formState, SetFormState] = useState(getDefaultState);
    localStorageHelper.set(STORAGE_KEY.AREA_CODE, '');

    const isContractSalesCNSite = useMemo(() => props.type === FormType.contractSales && isCn(), [props.type]);

    useEffect(() => {
        adapterService.init();
    }, []);

    const lastNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.lastName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const firstNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.firstName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const enterpriseNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.enterpriseName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const mailChangeCallback = (data: string) => {
        SetFormState((pre) => {
            pre.email = data;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const positionNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.positionName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const positionNameSelectChangeCallback = (item: OptionItemType) => {
        const value = item.id;
        SetFormState((pre) => {
            pre.positionName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const phoneChangeCallback = (data: IChangeCallbackArgs) => {
        SetFormState((pre) => {
            pre.codePosition = `${data.selectedCountryPosition}`;
            pre.areaCode = `${data.countryCode}`;
            pre.phone = `${data.phoneNumber}`;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const descriptionChangeCallback = (value: string) => {
        value = value.trim();
        SetFormState((pre) => {
            pre.description = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const departmentChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.department = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const staffCountChangeCallback = (item: OptionItemType) => {
        const value = item.id;
        SetFormState((pre) => {
            pre.staffCount = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const receiveSubscribeChangeCallback = (e: CheckboxChangeEvent) => {
        const {
            target: { checked },
        } = e;
        SetFormState((pre) => {
            pre.receiveSubscribe = checked;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const checkLastName = (lastName: string) => {
        const result = (lastName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showLastName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };

    const checkFirstName = (firstName: string) => {
        const result = (firstName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showFirstName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };

    const checkMail = (email: string) => {
        const result = validateMail(email);
        SetFormState((pre) => {
            pre.showMailMsg = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkEnterpriseName = (enterpriseName: string) => {
        const result = (enterpriseName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showEnterpriseName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkPositionName = (positionName: string) => {
        const result = (positionName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showPositionName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };

    // ========== CN site (isContractSalesCNSite) specific validation ==========

    /** 批量更新表单状态（仅用于 CN 校验） */
    const setCNFieldState = (updates: Record<string, unknown>) => {
        SetFormState((pre) => Object.assign({}, pre, updates));
    };

    const checkLastNameCN = (lastName: string) => {
        const trimmed = (lastName || '').trim();
        if (!trimmed) {
            setCNFieldState({ showLastName: true, lastNameErrorMsg: lang('姓不能为空') });
            return false;
        }
        if (trimmed.length > 20 || !CN_NAME_REGEX.test(trimmed)) {
            setCNFieldState({ showLastName: true, lastNameErrorMsg: lang('请输入有效的姓') });
            return false;
        }
        setCNFieldState({ showLastName: false, lastNameErrorMsg: '' });
        return true;
    };

    const checkFirstNameCN = (firstName: string) => {
        const trimmed = (firstName || '').trim();
        if (!trimmed) {
            setCNFieldState({ showFirstName: true, firstNameErrorMsg: lang('名不能为空') });
            return false;
        }
        if (trimmed.length > 20 || !CN_NAME_REGEX.test(trimmed)) {
            setCNFieldState({ showFirstName: true, firstNameErrorMsg: lang('请输入有效的名') });
            return false;
        }
        setCNFieldState({ showFirstName: false, firstNameErrorMsg: '' });
        return true;
    };

    const checkPhoneCN = (phone: string) => {
        const trimmed = (phone || '').trim();
        if (!trimmed) {
            setCNFieldState({ showPhoneMsg: true, phoneErrorMsg: lang('手机号码不能为空') });
            return false;
        }
        if (!/^1[3-9]\d{9}$/.test(trimmed)) {
            setCNFieldState({ showPhoneMsg: true, phoneErrorMsg: lang('请输入正确的11位手机号码') });
            return false;
        }
        setCNFieldState({ showPhoneMsg: false, phoneErrorMsg: '' });
        return true;
    };

    const checkMailCN = (email: string) => {
        const trimmed = (email || '').trim();
        // 电子邮箱在 CN 站点为选填，为空时直接通过
        if (!trimmed) {
            setCNFieldState({ showMailMsg: false, mailErrorMsg: '' });
            return true;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setCNFieldState({ showMailMsg: true, mailErrorMsg: lang('邮箱格式不正确') });
            return false;
        }
        setCNFieldState({ showMailMsg: false, mailErrorMsg: '' });
        return true;
    };

    const checkEnterpriseNameCN = (enterpriseName: string) => {
        const trimmed = (enterpriseName || '').trim();
        if (!trimmed) {
            setCNFieldState({ showEnterpriseName: true, enterpriseNameErrorMsg: lang('企业或组织名称不能为空') });
            return false;
        }
        // 禁止纯数字或特殊符号，长度 2-50
        if (!CN_HAS_LETTER_REGEX.test(trimmed) || trimmed.length < 2 || trimmed.length > 50) {
            setCNFieldState({ showEnterpriseName: true, enterpriseNameErrorMsg: lang('请填写正确的企业或组织名称') });
            return false;
        }
        setCNFieldState({ showEnterpriseName: false, enterpriseNameErrorMsg: '' });
        return true;
    };

    const checkPositionNameCN = (positionName: string) => {
        const trimmed = (positionName || '').trim();
        if (!trimmed) {
            setCNFieldState({ showPositionName: true, positionNameErrorMsg: lang('职位不能为空') });
            return false;
        }
        // 禁止纯数字或特殊符号，长度 2-20
        if (!CN_HAS_LETTER_REGEX.test(trimmed) || trimmed.length < 2 || trimmed.length > 20) {
            setCNFieldState({ showPositionName: true, positionNameErrorMsg: lang('请填写正确的职位信息') });
            return false;
        }
        setCNFieldState({ showPositionName: false, positionNameErrorMsg: '' });
        return true;
    };

    const checkDescriptionCN = (description: string) => {
        const trimmed = (description || '').trim();
        // 需求描述为选填，为空时直接通过
        if (!trimmed) {
            setCNFieldState({ showDescriptionMsg: false, descriptionErrorMsg: '' });
            return true;
        }
        if (trimmed.length > 200) {
            setCNFieldState({ showDescriptionMsg: true, descriptionErrorMsg: lang('超出长度，请控制在200字以内') });
            return false;
        }
        setCNFieldState({ showDescriptionMsg: false, descriptionErrorMsg: '' });
        return true;
    };

    const resetState = () => {
        const eles = $document.querySelectorAll('input');
        eles.forEach((ele) => {
            if (ele.type === 'text' || ele.type === 'email' || ele.type === 'tel') {
                ele.value = '';
            }
        });
        document.querySelectorAll('textarea').forEach((ele) => {
            ele.value = '';
        });
        SetFormState((pre) => {
            pre = getDefaultState;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };

    const submitFormSuccess = () => {
        switch (props.type) {
            case FormType.contractSales:
                if (isSystem('CN')) {
                    // 跳转thankyou页面
                    goThankYou();
                } else {
                    CustomDialog({
                        className: 'cotact-sales-dialog',
                        canClose: true,
                        width: '4rem',
                        childProps: {
                            resetState,
                        },
                        contentComponent: props.contentComponent,
                    });
                }
                return;
            case FormType.brandGuide:
            case FormType.whitePaper:
            case FormType.practiceMakesPerformance:
            case FormType.aiEmpowermentOrganizationalTransformation:
            case FormType.aiFutureOfWork:
            case FormType.talentTransformationAgeOfAi:
                if (props.downloadUrl) {
                    window.open(props.downloadUrl, '_blank');
                }
                resetState();
                props.changeHasSubmitted?.(true);
                return;
        }
    };

    const setPropertyOfChannelType = (params: ParamsType) => {
        switch (props.type) {
            case FormType.whitePaper:
            case FormType.brandGuide:
            case FormType.practiceMakesPerformance:
            case FormType.aiEmpowermentOrganizationalTransformation:
            case FormType.aiFutureOfWork:
            case FormType.talentTransformationAgeOfAi:
                params.channel_type = ChannelType[props.type];
        }
    };

    const submitForm = useDebounceFn(
        () => {
            const {
                lastName,
                firstName,
                phone,
                email,
                enterpriseName,
                positionName,
                areaCode,
                description,
                department,
                staffCount,
                receiveSubscribe,
            } = formState;

            // 提交前先清除上一轮校验状态，避免残留错误影响本次校验
            let hasError = false;
            if (isContractSalesCNSite) {
                setCNFieldState({
                    showLastName: false,
                    lastNameErrorMsg: '',
                    showFirstName: false,
                    firstNameErrorMsg: '',
                    showPhoneMsg: false,
                    phoneErrorMsg: '',
                    showMailMsg: false,
                    mailErrorMsg: '',
                    showEnterpriseName: false,
                    enterpriseNameErrorMsg: '',
                    showPositionName: false,
                    positionNameErrorMsg: '',
                    showDescriptionMsg: false,
                    descriptionErrorMsg: '',
                });
                // 短路校验：只校验到首个异常字段即停止
                hasError = !(
                    checkLastNameCN(lastName) &&
                    checkFirstNameCN(firstName) &&
                    checkPhoneCN(phone) &&
                    checkMailCN(email) &&
                    checkEnterpriseNameCN(enterpriseName) &&
                    checkPositionNameCN(positionName) &&
                    checkDescriptionCN(description)
                );
            } else {
                SetFormState((pre) =>
                    Object.assign({}, pre, {
                        showLastName: false,
                        showFirstName: false,
                        showMailMsg: false,
                        showEnterpriseName: false,
                        showPositionName: false,
                    }),
                );
                hasError = !(
                    checkLastName(lastName) &&
                    checkFirstName(firstName) &&
                    checkMail(email) &&
                    checkEnterpriseName(enterpriseName) &&
                    checkPositionName(positionName)
                );
            }

            // 仅在触发提交，且存在校验异常时滚动到视口
            if (hasError) {
                // 异步队列中执行滚动，避免首次校验状态还未更新完成，找不对对应的 error element
                Promise.resolve()
                    .then(() => {
                        scrollIntoView();
                    })
                    .catch((error: unknown) => {
                        console.error('Form scrollIntoView error:', error);
                    });
                return;
            }

            const params: any = {
                first_name: firstName,
                last_name: lastName,
                phone: isContractSalesCNSite ? `${phone}` : `${areaCode}-${phone}`,
                email,
                enterprise_name: enterpriseName,
                position_name: positionName,
                source: props.referUrl,
                description,
                department,
                staff_count: staffCount,
            };

            if (props.type === FormType.contractSales && (isCom() || isIo())) {
                params.receive_subscribe = Number(receiveSubscribe);
            }
            if (isCn()) {
                // CN 站点线索上报固定参数，不需要翻译
                params.lead_source = 'Website Form';
                params.acquisition_source__c = props.acquisitionSource;
            }

            setPropertyOfChannelType(params);
            contactSales(params)
                .then((res: IOutputContactSales) => {
                    if (res.data) {
                        submitFormSuccess();
                    } else {
                        resetState();
                    }
                })
                .catch((error: unknown) => {
                    handlePromiseError(error, 'Form.contactSales', 'Form contactSales error', {
                        ...params,
                        formType: props.type,
                    });
                });
            adapterService.track(params, props.type);
        },
        { wait: 3000, leading: true, trailing: false },
    );

    const isEn = useMemo(() => {
        return _lang('en');
    }, []);

    const getPlaceHolder = (ph?: string) => {
        if (isEn && !isContractSalesCNSite) {
            return props.platform === 'twww' ? '' : ph;
        }
        return ph;
    };

    const getPlaceHolderByPlatform = (ph?: string, mph?: string) => {
        return props.platform === 'twww' ? ph : mph;
    };

    const getMessagePlaceholder = () => {
        if (isContractSalesCNSite) {
            return lang('请简要描述您的需求，如“企业培训”、“经理辅导”等');
        }
        if (isEn) {
            return getPlaceHolderByPlatform('Optional', 'Message (optional)');
        }
        return props.platform === 'twww' ? lang('请填写留言') : lang('请填写留言(选填)');
    };

    const lastNameElement = (
        <div
            key={'lastNameElement'}
            className={formState.showLastName ? 'input name last-name error' : 'input name last-name'}
        >
            <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                <label className="label-title" htmlFor="form-last-name">
                    {lang('姓')}
                    <i className="icon icon-required" />
                </label>
            </If>
            <input
                id="form-last-name"
                type="text"
                name="last_name"
                autoComplete="family-name"
                aria-required
                aria-invalid={formState.showLastName}
                aria-describedby={formState.showLastName ? 'form-last-name-error' : undefined}
                placeholder={getPlaceHolder(lang('请填写您的姓'))}
                defaultValue={formState.lastName}
                onChange={lastNameChangeCallback}
            />

            <If condition={formState.showLastName}>
                <p id="form-last-name-error" className="form-tip error">
                    {isContractSalesCNSite ? formState.lastNameErrorMsg : lang('请填写您的姓')}
                </p>
            </If>
        </div>
    );

    const firstNameElement = (
        <div key={'firstNameElement'} className={formState.showFirstName ? 'input name error' : 'input name'}>
            <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                <label className="label-title" htmlFor="form-first-name">
                    {lang('名')}
                    <i className="icon icon-required" />
                </label>
            </If>
            <input
                id="form-first-name"
                type="text"
                name="first_name"
                autoComplete="given-name"
                aria-required
                aria-invalid={formState.showFirstName}
                aria-describedby={formState.showFirstName ? 'form-first-name-error' : undefined}
                defaultValue={formState.firstName}
                onChange={firstNameChangeCallback}
                placeholder={getPlaceHolder(lang('请填写您的名'))}
            />

            <If condition={formState.showFirstName}>
                <p id="form-first-name-error" className="form-tip error">
                    {isContractSalesCNSite ? formState.firstNameErrorMsg : lang('请填写您的名')}
                </p>
            </If>
        </div>
    );

    const getUserNameForms = () => {
        if (isEn) {
            return [firstNameElement, lastNameElement];
        }

        return [lastNameElement, firstNameElement];
    };

    const renderButton = () => {
        switch (props.type) {
            case FormType.contractSales:
                return (
                    <Button className="contract-sales-btn" onClick={() => submitForm.run()}>
                        {isEn ? 'Submit' : lang('联系 UMU 销售')}
                    </Button>
                );

            case FormType.brandGuide:
                return (
                    <Button className="white-paper-btn brandGuide" onClick={() => submitForm.run()}>
                        {lang('下载 UMU 品牌素材')}
                    </Button>
                );

            case FormType.whitePaper:
                return (
                    <Button className="white-paper-btn" onClick={() => submitForm.run()}>
                        {lang('马上获取')}
                    </Button>
                );

            case FormType.tappingThePotentialOfSE:
                if (props.platform === 'twww') {
                    return <Button className="tapping-the-potential-of-se twww">Download</Button>;
                }
                return <Button className="tapping-the-potential-of-se mobile">Submit</Button>;
            case FormType.practiceMakesPerformance:
                return (
                    <Button
                        className={`practice-makes-performance-btn ${props.platform === 'mobile' && 'mobile'}`}
                        onClick={() => submitForm.run()}
                    >
                        {isEn ? (props.platform === 'mobile' ? 'Submit' : 'Download') : lang('马上获取')}
                    </Button>
                );
            case FormType.aiEmpowermentOrganizationalTransformation:
                return (
                    <Button
                        className={`ai-empowerment-organizational-transformation-btn ${
                            props.platform === 'mobile' && 'mobile'
                        }`}
                        onClick={() => submitForm.run()}
                    >
                        {lang('Download')}
                    </Button>
                );
            case FormType.aiFutureOfWork:
                return (
                    <Button
                        className={`ai-future-of-work-btn ${props.platform === 'mobile' && 'mobile'}`}
                        onClick={() => submitForm.run()}
                    >
                        {lang('Download')}
                    </Button>
                );
            case FormType.talentTransformationAgeOfAi:
                return (
                    <Button
                        className={`talent-transformation-age-of-ai-btn ${props.platform === 'mobile' && 'mobile'}`}
                        onClick={() => submitForm.run()}
                    >
                        {lang('Download')}
                    </Button>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        document.body.classList.add('form-body');
        return () => {
            document.body.classList.remove('form-body');
        };
    }, []);

    return (
        <form {...props.fromProps} onSubmit={(e) => e.preventDefault()} noValidate>
            <div className="form-row form-user-row">{getUserNameForms()}</div>
            <div className={`form-row${isContractSalesCNSite && formState.showPhoneMsg ? ' error' : ''}`}>
                <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                    <label className="label-title" htmlFor="tel-phone">
                        {isEn ? 'Phone number' : lang('手机号码')}
                        <If condition={isContractSalesCNSite}>
                            <i className="icon icon-required" />
                        </If>
                    </label>
                </If>
                <PhoneInput
                    initialPhone={formState.phone}
                    initialValue={formState.areaCode}
                    initialPosition={formState.codePosition}
                    hidePhoneArea={!!props.hidePhoneArea}
                    changeCallback={phoneChangeCallback}
                    placeholder={
                        isContractSalesCNSite
                            ? lang('请输入您的手机号')
                            : isEn
                            ? getPlaceHolderByPlatform('Optional', 'Phone (optional)')
                            : getPlaceHolder()
                    }
                />
                <If condition={isContractSalesCNSite && formState.showPhoneMsg}>
                    <p className="form-tip error">{formState.phoneErrorMsg}</p>
                </If>
            </div>
            <div className={formState.showMailMsg ? 'form-row mail-input-wrap error' : 'form-row mail-input-wrap'}>
                <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                    <label className="label-title" htmlFor="email">
                        {isEn ? 'Work email' : lang('电子邮箱')}
                        <If condition={!isContractSalesCNSite}>
                            <i className="icon icon-required" />
                        </If>
                    </label>
                </If>
                <MailInput
                    initialVal={formState.email}
                    isShowSuggestion={false}
                    changeCallback={mailChangeCallback}
                    placeholder={
                        isContractSalesCNSite ? lang('请输入您的邮箱地址') : getPlaceHolder('name@company.com')
                    }
                />

                <If condition={formState.showMailMsg}>
                    <p className="form-tip error">
                        {isContractSalesCNSite ? formState.mailErrorMsg : lang('请输入有效的电子邮箱')}
                    </p>
                </If>
            </div>
            <div className={formState.showEnterpriseName ? 'form-row error' : 'form-row'}>
                <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                    <label className="label-title" htmlFor="form-enterprise-name">
                        {isContractSalesCNSite
                            ? lang('企业或组织名称')
                            : isEn
                            ? 'Company name'
                            : lang('企业或单位名称')}
                        <i className="icon icon-required" />
                    </label>
                </If>
                <div className="input">
                    <input
                        id="form-enterprise-name"
                        type="text"
                        name="enterprise_name"
                        autoComplete="organization"
                        aria-required
                        aria-invalid={formState.showEnterpriseName}
                        aria-describedby={formState.showEnterpriseName ? 'form-enterprise-name-error' : undefined}
                        defaultValue={formState.enterpriseName}
                        onChange={enterpriseNameChangeCallback}
                        placeholder={
                            isContractSalesCNSite
                                ? lang('请填写您的企业或者组织名称')
                                : getPlaceHolder(lang('请填写企业或单位名称'))
                        }
                    />
                </div>
                <If condition={formState.showEnterpriseName}>
                    <p id="form-enterprise-name-error" className="form-tip error">
                        {isContractSalesCNSite ? formState.enterpriseNameErrorMsg : lang('请填写企业或单位名称')}
                    </p>
                </If>
            </div>
            <If condition={isCoSite()}>
                <div className="form-row">
                    <If condition={props.platform === 'twww'}>
                        <label className="label-title" htmlFor="form-department">
                            {_lang('jp') ? '部署' : lang('部门')}
                        </label>
                    </If>
                    <div className="input">
                        <input
                            id="form-department"
                            type="text"
                            name="department"
                            autoComplete="organization"
                            defaultValue={formState.department}
                            onChange={departmentChangeCallback}
                            placeholder={getPlaceHolder(
                                _lang('jp') ? '部署名を入力してください' : lang('请填写部门名称'),
                            )}
                        />
                    </div>
                </div>
            </If>
            <div className={`${formState.showPositionName ? 'form-row error' : 'form-row'}`}>
                <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                    <label className="label-title" htmlFor="form-position-name">
                        {lang('职位')}
                        <i className="icon icon-required" />
                    </label>
                </If>
                <If condition={!isCoSite()}>
                    <div className="input">
                        <input
                            id="form-position-name"
                            type="text"
                            name="position_name"
                            autoComplete="organization-title"
                            aria-required
                            aria-invalid={formState.showPositionName}
                            aria-describedby={formState.showPositionName ? 'form-position-name-error' : undefined}
                            defaultValue={formState.positionName}
                            onChange={positionNameChangeCallback}
                            placeholder={
                                isContractSalesCNSite
                                    ? lang('请填写您的职位信息')
                                    : getPlaceHolder(lang('请填写您的职位'))
                            }
                        />
                    </div>
                </If>
                <If condition={isCoSite()}>
                    <div className="select">
                        <Select
                            selected={formState.positionName}
                            list={jobOptionList}
                            placeholder={getPlaceHolder(lang('请选择您的职位'))}
                            onSelect={positionNameSelectChangeCallback}
                        />
                    </div>
                </If>
                <If condition={formState.showPositionName}>
                    {isCoSite() ? (
                        <p className="form-tip error">{getPlaceHolder(lang('请选择您的职位'))}</p>
                    ) : (
                        <p id="form-position-name-error" className="form-tip error">
                            {isContractSalesCNSite ? formState.positionNameErrorMsg : lang('请填写您的职位')}
                        </p>
                    )}
                </If>
            </div>
            <If condition={isCoSite()}>
                <div className="form-row">
                    <If condition={props.platform === 'twww'}>
                        <label className="label-title">{lang('员工人数')}</label>
                    </If>
                    <div className="select">
                        <Select
                            selected={formState.staffCount}
                            list={amountOptionList}
                            placeholder={getPlaceHolder(lang('请选择员工人数'))}
                            onSelect={staffCountChangeCallback}
                        />
                    </div>
                </div>
            </If>
            <If
                condition={
                    ![
                        FormType.whitePaper,
                        FormType.practiceMakesPerformance,
                        FormType.aiEmpowermentOrganizationalTransformation,
                        FormType.aiFutureOfWork,
                        FormType.talentTransformationAgeOfAi,
                    ].includes(props.type)
                }
            >
                <div
                    className={`form-row comment-wrapper${
                        isContractSalesCNSite && formState.showDescriptionMsg ? ' error' : ''
                    }`}
                >
                    <If condition={props.platform === 'twww' || isContractSalesCNSite}>
                        <label className="label-title" htmlFor="describe">
                            {isContractSalesCNSite ? lang('需求描述') : isEn ? 'Message' : lang('留言')}
                        </label>
                    </If>
                    <div className="input">
                        <Textarea
                            textareaProps={{
                                id: 'describe',
                            }}
                            value={formState.description}
                            placeholder={getMessagePlaceholder()}
                            onChange={descriptionChangeCallback}
                            maxLength={isContractSalesCNSite ? 200 : _lang('cn') || _lang('jp') ? 1000 : 2000}
                        />
                    </div>
                    <If condition={isContractSalesCNSite && formState.showDescriptionMsg}>
                        <p className="form-tip error">{formState.descriptionErrorMsg}</p>
                    </If>
                </div>
            </If>
            <If condition={props.type === FormType.contractSales && (isCom() || isIo())}>
                <div className="form-row receive-subscribe-row">
                    <Checkbox checked={formState.receiveSubscribe} onChange={(e) => receiveSubscribeChangeCallback(e)}>
                        {lang('接收来自UMU的订阅材料')}
                    </Checkbox>
                </div>
            </If>
            <div className={`contract-sales-footer ${props.platform} ${props.type}`}>{renderButton()}</div>
        </form>
    );
}

export default Form;
